import { supabase } from '@/lib/supabaseClient';

function normalizeRecurringFrequency(value) {
  const allowed = ['none', 'daily', 'weekly', 'bi-weekly', 'monthly'];
  return allowed.includes(value) ? value : 'none';
}

function buildJobRecord(payload) {
  const record = {
    account_id: payload.accountId,
    client_id: payload.clientId,
    title: payload.title?.trim() || null,
    service_type: payload.serviceType?.trim(),
    notes: payload.notes?.trim() || null,
    status: payload.status || 'scheduled',
    start_at: payload.startAt,
    end_at: payload.endAt,
  };

  if (payload.includeRecurring !== false) {
    record.recurring_frequency = normalizeRecurringFrequency(payload.recurringFrequency);
    record.recurring_until = payload.recurringUntil || null;
  }

  return record;
}

function isMissingRecurringColumn(error) {
  const message = error?.message || '';
  return message.includes('recurring_frequency') || message.includes('recurring_until');
}

function getJobSelect() {
  return `
    *,
    client:clients (
      id,
      customer_name,
      property_address,
      customer_phone,
      customer_email
    )
  `;
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function addRecurringInterval(date, frequency) {
  const next = new Date(date);

  if (frequency === 'daily') {
    next.setDate(next.getDate() + 1);
  } else if (frequency === 'weekly') {
    next.setDate(next.getDate() + 7);
  } else if (frequency === 'bi-weekly') {
    next.setDate(next.getDate() + 14);
  } else if (frequency === 'monthly') {
    next.setMonth(next.getMonth() + 1);
  }

  return next;
}

function getFirstVisibleOccurrence(start, frequency, windowStart) {
  if (start >= windowStart) return new Date(start);

  if (frequency === 'daily' || frequency === 'weekly' || frequency === 'bi-weekly') {
    const intervalDays = frequency === 'daily' ? 1 : frequency === 'weekly' ? 7 : 14;
    const intervalMs = intervalDays * 24 * 60 * 60 * 1000;
    const elapsedIntervals = Math.floor((windowStart.getTime() - start.getTime()) / intervalMs);
    const candidate = new Date(start);
    candidate.setDate(candidate.getDate() + elapsedIntervals * intervalDays);
    return candidate < windowStart ? addRecurringInterval(candidate, frequency) : candidate;
  }

  const candidate = new Date(start);
  let guard = 0;
  while (candidate < windowStart && guard < 2400) {
    const next = addRecurringInterval(candidate, frequency);
    if (next <= candidate) break;
    candidate.setTime(next.getTime());
    guard += 1;
  }

  return candidate;
}

function buildOccurrence(job, occurrenceStart, durationMs) {
  const occurrenceEnd = new Date(occurrenceStart.getTime() + durationMs);
  const occurrenceStartIso = occurrenceStart.toISOString();

  return {
    ...job,
    start_at: occurrenceStartIso,
    end_at: occurrenceEnd.toISOString(),
    occurrence_key: `${job.id}:${occurrenceStartIso}`,
    source_job_id: job.id,
  };
}

function expandRecurringJobs(jobs, filters = {}) {
  const windowStart = parseDate(filters.startDate);
  const windowEnd = parseDate(filters.endDate);

  if (!windowStart || !windowEnd) {
    return jobs.map((job) => ({
      ...job,
      occurrence_key: job.id,
      source_job_id: job.id,
    }));
  }

  const expanded = [];

  for (const job of jobs) {
    const start = parseDate(job.start_at);
    const end = parseDate(job.end_at);
    if (!start || !end) continue;

    const durationMs = Math.max(end.getTime() - start.getTime(), 0);
    const frequency = normalizeRecurringFrequency(job.recurring_frequency);
    const recurringUntil = parseDate(job.recurring_until);

    if (frequency === 'none') {
      if (start >= windowStart && start < windowEnd) {
        expanded.push(buildOccurrence(job, start, durationMs));
      }
      continue;
    }

    if (recurringUntil && recurringUntil < windowStart) {
      continue;
    }

    let occurrenceStart = getFirstVisibleOccurrence(start, frequency, windowStart);
    let guard = 0;

    while (occurrenceStart < windowEnd && guard < 1000) {
      if ((!recurringUntil || occurrenceStart <= recurringUntil) && occurrenceStart >= windowStart) {
        expanded.push(buildOccurrence(job, occurrenceStart, durationMs));
      }

      const nextOccurrence = addRecurringInterval(occurrenceStart, frequency);
      if (nextOccurrence <= occurrenceStart) break;
      occurrenceStart = nextOccurrence;
      guard += 1;
    }
  }

  return expanded.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
}

export async function getScheduledJobs(accountId, filters = {}) {
  if (!accountId) return [];

  let query = supabase
    .from('scheduled_jobs')
    .select(getJobSelect())
    .eq('account_id', accountId)
    .order('start_at', { ascending: true });

  if (filters.clientId) {
    query = query.eq('client_id', filters.clientId);
  }

  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  if (filters.endDate) {
    query = query.lt('start_at', filters.endDate);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[JobService] Error fetching scheduled jobs:', error);
    throw new Error('Failed to load scheduled jobs: ' + error.message);
  }

  return expandRecurringJobs(data || [], filters);
}

export async function createScheduledJob(payload) {
  const insertJob = (record) => supabase
    .from('scheduled_jobs')
    .insert([record])
    .select(getJobSelect())
    .single();

  let { data, error } = await insertJob(buildJobRecord(payload));

  if (error && isMissingRecurringColumn(error)) {
    console.warn('[JobService] Recurring columns are missing. Retrying job create without recurrence metadata.');
    ({ data, error } = await insertJob(buildJobRecord({ ...payload, includeRecurring: false })));
  }

  if (error) {
    console.error('[JobService] Error creating scheduled job:', error);
    throw new Error('Failed to create job: ' + error.message);
  }

  return data;
}

export async function updateScheduledJob(jobId, updates) {
  const patch = {};

  if (updates.title !== undefined) patch.title = updates.title?.trim() || null;
  if (updates.clientId !== undefined) patch.client_id = updates.clientId;
  if (updates.serviceType !== undefined) patch.service_type = updates.serviceType?.trim();
  if (updates.notes !== undefined) patch.notes = updates.notes?.trim() || null;
  if (updates.status !== undefined) patch.status = updates.status;
  if (updates.startAt !== undefined) patch.start_at = updates.startAt;
  if (updates.endAt !== undefined) patch.end_at = updates.endAt;
  if (updates.recurringFrequency !== undefined) patch.recurring_frequency = normalizeRecurringFrequency(updates.recurringFrequency);
  if (updates.recurringUntil !== undefined) patch.recurring_until = updates.recurringUntil || null;

  const updateJob = (nextPatch) => supabase
    .from('scheduled_jobs')
    .update(nextPatch)
    .eq('id', jobId)
    .select(getJobSelect())
    .single();

  let { data, error } = await updateJob(patch);

  if (error && isMissingRecurringColumn(error)) {
    console.warn('[JobService] Recurring columns are missing. Retrying job update without recurrence metadata.');
    const { recurring_frequency, recurring_until, ...patchWithoutRecurring } = patch;
    ({ data, error } = await updateJob(patchWithoutRecurring));
  }

  if (error) {
    console.error('[JobService] Error updating scheduled job:', error);
    throw new Error('Failed to update job: ' + error.message);
  }

  return data;
}

export async function deleteScheduledJob(jobId) {
  const { error } = await supabase
    .from('scheduled_jobs')
    .delete()
    .eq('id', jobId);

  if (error) {
    console.error('[JobService] Error deleting scheduled job:', error);
    throw new Error('Failed to delete job: ' + error.message);
  }

  return true;
}
